import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { KeyRound, XCircle } from "lucide-react";
import { toast } from "sonner";
// Logo sesuai latar terang/gelap (sama dgn AuthShell) + tagline sbg teks (lib/brand.js); logo.png lama
// berwarna putih (utk sidebar teal) & memuat tagline lama di dalam gambar.
import logoTerang from "../assets/aura-logo-terang.png";
import logoGelap from "../assets/aura-logo-gelap.png";
import { TAGLINE } from "../lib/brand";
import PasswordInput from "@/components/PasswordInput";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid } from "@/lib/passwordRules";

// Halaman publik dituju dari link di email reset password (?token=..., langsung
// dihapus dari bilah alamat setelah dibaca) -- dipicu
// admin lewat "Kirim Link Reset" di menu Akun Admin, atau self-service "Lupa
// Password?" di halaman login (keduanya menghasilkan link ke halaman yg SAMA ini).
// Token TIDAK pernah disimpan sbg state navigasi (beda dari otp_token di
// VerifyOtp.jsx) krn diakses langsung dari klik link email, bukan lewat form di
// app ini.
//
// Halaman ini SENGAJA TIDAK dibungkus PublicOnly di App.js: harus selalu tampil
// apapun status login browser (link bisa dibuka di komputer bersama yg sedang
// login akun lain), makanya sesi aktif dikeluarkan sendiri di sini (clearSession)
// sebelum apapun lain ditampilkan. Validitas token juga dicek SAAT HALAMAN
// DIBUKA lewat endpoint read-only (validateResetToken) -- bukan cuma saat submit
// -- supaya link yg sudah dipakai/kedaluwarsa langsung ketahuan tanpa user perlu
// mengisi form dulu. Pengecekan itu TIDAK menghabiskan token; token baru
// benar-benar terpakai saat resetPasswordConfirm dipanggil di submit.
export default function ResetPassword() {
    const { clearSession, validateResetToken, resetPasswordConfirm } = useAuth();
    const [searchParams] = useSearchParams();
    // Token dibaca SEKALI dari link email ke state, lalu langsung dihapus dari bilah
    // alamat (efek di bawah) supaya tidak tertinggal di riwayat browser / terkirim
    // sbg Referer. Ke backend token selalu lewat body POST, tidak pernah di URL.
    const [token] = useState(() => searchParams.get("token"));
    const [checking, setChecking] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    // Ganti tiap submit (sukses/gagal) -> me-remount kedua PasswordInput di bawah,
    // memaksa kolomnya balik tersembunyi (lihat PasswordInput.jsx).
    const [pwResetKey, setPwResetKey] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) return;
        if (searchParams.has("token")) navigate("/reset-password", { replace: true });
        clearSession();
        let cancelled = false;
        (async () => {
            const valid = await validateResetToken(token);
            if (cancelled) return;
            setTokenValid(valid);
            setChecking(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (!token) return <Navigate to="/login" replace />;

    const submit = async (e) => {
        e.preventDefault();
        if (!isPasswordValid(next)) return toast.error("Kata sandi baru belum memenuhi semua syarat di bawah");
        if (next !== confirm) return toast.error("Konfirmasi kata sandi tidak cocok");
        setLoading(true);
        const res = await resetPasswordConfirm(token, next);
        setLoading(false);
        setPwResetKey((k) => k + 1);
        if (!res.ok) return toast.error(res.error);
        toast.success("Kata sandi berhasil diperbarui. Silakan login dengan kata sandi baru.");
        setDone(true);
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <div className="brand-gradient text-white px-8 py-6">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center border border-white/20">
                            <KeyRound className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-white font-semibold">Reset Kata Sandi</div>
                            <h2 className="text-xl font-extrabold" style={{ fontFamily: "Plus Jakarta Sans" }}>
                                Buat Kata Sandi Baru
                            </h2>
                        </div>
                    </div>
                </div>

                <div className="px-8 py-6">
                    {checking ? (
                        <p className="text-sm text-slate-500 text-center py-6" data-testid="reset-password-checking">
                            Memeriksa link...
                        </p>
                    ) : done ? (
                        <div className="text-center space-y-4 py-4">
                            <div className="flex flex-col items-center gap-1" data-testid="reset-brand">
                                <img src={logoTerang} alt="AURA PPDS" className="block w-32 h-auto dark:hidden" />
                                <img src={logoGelap} alt="AURA PPDS" className="hidden w-32 h-auto dark:block" />
                                <p className="text-xs text-slate-500 text-balance">{TAGLINE}</p>
                            </div>
                            <p className="text-slate-600 text-sm">
                                Kata sandi Anda sudah diperbarui. Semua sesi login lama akun ini sudah tidak berlaku lagi.
                            </p>
                            <Button
                                data-testid="reset-password-to-login-button"
                                onClick={() => navigate("/login")}
                                className="h-11 bg-[#00B9AD] hover:bg-[#60C0D0]"
                            >
                                Masuk Sekarang
                            </Button>
                        </div>
                    ) : !tokenValid ? (
                        <div className="text-center space-y-4 py-4">
                            <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto">
                                <XCircle className="w-7 h-7 text-rose-500" />
                            </div>
                            <p className="text-slate-600 text-sm" data-testid="reset-password-invalid-message">
                                Link reset tidak valid atau sudah tidak berlaku. Silakan hubungi UFP RSCM.
                            </p>
                            <Button
                                data-testid="reset-password-back-to-login-button"
                                onClick={() => navigate("/login")}
                                variant="outline"
                                className="h-11"
                            >
                                Kembali ke Login
                            </Button>
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-slate-500 mb-5">
                                Buat kata sandi baru untuk akun Anda. Link ini hanya bisa dipakai satu kali.
                            </p>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Kata Sandi Baru</Label>
                                    <PasswordInput
                                        key={`next-${pwResetKey}`}
                                        data-testid="reset-password-new-input"
                                        required
                                        minLength={8}
                                        autoFocus
                                        autoComplete="new-password"
                                        value={next}
                                        onChange={(e) => setNext(e.target.value)}
                                        className="mt-1.5 h-11"
                                    />
                                    <PasswordRequirements password={next} />
                                </div>
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-700">Konfirmasi Kata Sandi Baru</Label>
                                    <PasswordInput
                                        key={`confirm-${pwResetKey}`}
                                        data-testid="reset-password-confirm-input"
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        className="mt-1.5 h-11"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    data-testid="reset-password-submit-button"
                                    disabled={loading}
                                    className="w-full h-11 bg-[#00B9AD] hover:bg-[#60C0D0]"
                                >
                                    {loading ? "Menyimpan..." : "Simpan Kata Sandi Baru"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
